import React from 'react';
import SalvPage from '../../../common/SalvPage';
import { InternalApiEntityGroup, InternalApiEntityType, InternalApiScenarioLanguage } from '../../../api-internal';
import { InternalNavigation } from '../../../navigation/helper';
import { useDeleteSegment, useQuerySegment, useRecalculateSegment, useUpdateSegment } from './segmentActions';
import { useNavigate, useParams } from 'react-router';
import {
  MenuItem,
  SalvButton,
  SalvDropdown,
  SalvFieldHeader,
  SalvMenuButton,
  SalvProgressCircle,
  SalvTextField,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import { createSearchParams } from 'react-router-dom';
import { SearchParamKeys } from '../../../common/searchParamKeys';
import { Form, Formik, FormikProps } from 'formik';
import { SegmentFormValues } from './SegmentEditor';
import * as Yup from 'yup';
import { editorLanguageOptions } from '../../../common/editor/EditorLayout';
import Editor from '../../../common/editor/Editor';
import './Segment.scss';
import { CAN_TRIGGER_ENTITY_GROUP_RECALCULATION, CAN_MODIFY_ENTITY_GROUP } from '../../../auth/permissions';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import { useMe } from '../../../organisation/useMe';
import { faBoxArchive } from '@fortawesome/pro-solid-svg-icons/faBoxArchive';
import SegmentResults from './SegmentResults';
import Has from '../../../auth/Has';
import SegmentEvents from './SegmentEvents';
import { FullCalculationLabel } from './helper';

const SegmentDataWrapper = () => {
  const dispatch = useDispatch();
  const code = useParams<{ code: string }>().code!;
  const { data: segment, isPending, isError } = useQuerySegment(code);
  if (isError) {
    dispatch(
      showFeedback({
        message: 'Could not load Segment',
        success: false,
      })
    );
    return <div />;
  }

  return isPending ? (
    <SalvPage
      title={<SalvProgressCircle />}
      breadcrumb={{
        title: 'Segments',
        to: {
          pathname: InternalNavigation.segments,
        },
      }}
    />
  ) : (
    <Segment segment={segment} />
  );
};

interface SegmentProps {
  segment: InternalApiEntityGroup;
}
const Segment = ({ segment }: SegmentProps) => {
  const { permissions } = useMe();
  const navigate = useNavigate();

  const { mutate: updateSegment } = useUpdateSegment();
  const { mutateAsync: deleteSegment } = useDeleteSegment();
  const { mutate: recalculateSegment } = useRecalculateSegment(segment);

  const linkToSegments = {
    pathname: InternalNavigation.segments,
    search: createSearchParams({
      [SearchParamKeys.entityType]: segment.entityType,
    }).toString(),
  };

  const submit = async (values: SegmentFormValues) => {
    const segment: InternalApiEntityGroup = values;
    segment.name = segment.name.trim();
    updateSegment(segment);
    navigate(linkToSegments);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Please provide a name'),
  });

  return (
    <Formik
      initialValues={{
        id: segment.id,
        name: segment.name,
        code: segment.code!,
        rule: segment.rule,
        entityType: segment.entityType,
      }}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, errors }: FormikProps<SegmentFormValues>) => (
        <Form style={{ height: '100%' }}>
          <SalvPage
            title={
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span>{segment.name}</span>
                <FullCalculationLabel segment={segment} />
              </div>
            }
            breadcrumb={{
              title: 'Segments',
              to: linkToSegments,
            }}
            actions={
              <>
                <Has allPermissions={CAN_TRIGGER_ENTITY_GROUP_RECALCULATION}>
                  <SalvTooltipWrapper
                    tooltip={'Segment is currently being calculated'}
                    isDisabled={segment.fullCalculationState === 'FINISHED'}
                  >
                    {segment.entityType === InternalApiEntityType.PERSON ? (
                      <SalvButton
                        label={'Full Calculation'}
                        icon={faArrowsRotate}
                        variant='muted'
                        size={Size.SMALL}
                        onClick={() => recalculateSegment(30)}
                        disabled={segment.fullCalculationState === 'STARTED'}
                      />
                    ) : (
                      <SalvMenuButton
                        label={'Full Calculation'}
                        icon={faArrowsRotate}
                        size={Size.SMALL}
                        onAction={([value]) => recalculateSegment(Number(value))}
                        disabled={segment.fullCalculationState === 'STARTED'}
                        placement={'bottom end'}
                      >
                        <MenuItem title={'Last 7 days'} value={'7'} icon={faArrowsRotate} />
                        <MenuItem title={'Last 30 days'} value={'30'} icon={faArrowsRotate} />
                        <MenuItem title={'Last 90 days'} value={'90'} icon={faArrowsRotate} />
                      </SalvMenuButton>
                    )}
                  </SalvTooltipWrapper>
                </Has>
                <Has allPermissions={CAN_MODIFY_ENTITY_GROUP}>
                  <SalvButton label={'Save changes'} size={Size.SMALL} type={'submit'} />
                  <SalvButton
                    icon={faBoxArchive}
                    variant='danger-outline'
                    label='Archive'
                    size={Size.SMALL}
                    onClick={async () => {
                      const userDecision = await deleteSegment(segment);
                      if (userDecision === 'confirmed') {
                        navigate(linkToSegments);
                      }
                    }}
                  />
                </Has>
              </>
            }
            className={'salv-segment'}
          >
            <div style={{ display: 'flex', height: '100%' }}>
              <div
                style={{
                  flex: '1 1 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  padding: '16px',
                  overflow: 'auto',
                }}
              >
                <div>
                  <SalvTextField
                    label='Name'
                    size={Size.MEDIUM}
                    className='m-b-4'
                    value={values.name}
                    onChange={async (value: string) => {
                      await setFieldValue('name', value);
                    }}
                    disabled={!permissions.includes(CAN_MODIFY_ENTITY_GROUP)}
                  />
                  {errors.name && <div className={'salv-error-message'}>{errors.name}</div>}
                </div>
                <div>
                  <SalvTextField label='Code' size={Size.MEDIUM} className='m-b-4' disabled value={values.code} />
                </div>
                <div>
                  <SalvFieldHeader label='Type' size={Size.MEDIUM} className='m-b-8' />
                  <div className={'text-m'}>
                    {values.entityType[0].toUpperCase() + values.entityType.slice(1).toLowerCase()}
                  </div>
                </div>

                <div>
                  <SalvFieldHeader label='Rule' size={Size.MEDIUM} />
                  <SalvDropdown
                    label='Language'
                    size={Size.SMALL}
                    options={editorLanguageOptions}
                    value={editorLanguageOptions.find(
                      (option) => option.value === InternalApiScenarioLanguage.JMESPATH
                    )}
                    disabled
                    clearable={false}
                    className={'m-b-8'}
                  />
                  <Editor
                    disabled
                    value={values.rule}
                    onChange={(_) => {}}
                    type={InternalApiScenarioLanguage.JMESPATH}
                  />
                </div>
              </div>
              <div className={'vertical-separator'} />
              <div style={{ flex: '1 1 0', overflow: 'auto' }}>
                <div style={{ padding: '16px' }}>
                  <SegmentResults segment={segment} />
                </div>
                <div className={'horizontal-separator'} />
                <div style={{ padding: '16px' }}>
                  <SegmentEvents segment={segment} />
                </div>
              </div>
            </div>
          </SalvPage>
        </Form>
      )}
    </Formik>
  );
};

export default SegmentDataWrapper;
